import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchJson } from '../api';
import CategoryGrid from '../components/CategoryGrid';
import { generateWebsiteSchema, generateOrganizationSchema, generateBreadcrumbSchema, generateSchemaGraph } from '../utils/schema';
import { generateCompleteSEO, generateLocationKeywords } from '../utils/seo';
import SEOHead from '../components/SEOHead';
import { getSiteBaseUrl } from '../config/site';

const CitySchema = ({ stateSlug, citySlug, cityName, stateName, categories }) => {
  const baseUrl = getSiteBaseUrl();
  const pageUrl = `${baseUrl}/in/${stateSlug}/${citySlug}`;
  
  const schema = generateSchemaGraph([
    generateWebsiteSchema(),
    generateOrganizationSchema(),
    generateBreadcrumbSchema([
      { name: 'Home', url: baseUrl },
      { name: stateName, url: `${baseUrl}/in/${stateSlug}` },
      { name: cityName, url: pageUrl }
    ]),
    {
      '@type': 'CollectionPage',
      '@id': `${pageUrl}/#webpage`,
      url: pageUrl,
      name: `Best Businesses in ${cityName}, ${stateName} | Visibeen Directory`,
      description: `Find ${categories?.length || 0}+ business categories in ${cityName}, ${stateName}. Browse reviews, ratings and contact information.`,
      isPartOf: { '@id': `${baseUrl}/#website` },
      about: {
        '@type': 'City',
        name: cityName,
        containedInPlace: {
          '@type': 'AdministrativeArea',
          name: stateName
        }
      },
      mainEntity: {
        '@type': 'ItemList',
        name: `Business Categories in ${cityName}`,
        numberOfItems: categories?.length || 0,
        itemListElement: (categories || []).slice(0, 20).map((cat, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: cat.name || cat.slug,
          url: `${baseUrl}/in/${stateSlug}/${citySlug}/${cat.slug}`
        }))
      }
    }
  ]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default function CityPage() {
  const { stateSlug, citySlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetchJson(`/api/v1/directory/in/${stateSlug}/${citySlug}/categories`);
        setData(res);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [stateSlug, citySlug]);

  if (loading) return <div>Loading categories…</div>;
  if (error) return <div className="error">{error}</div>;

  const cityName = data?.city?.name || citySlug;
  const stateName = data?.state?.name || stateSlug;
  const categories = data?.categories || [];

  const formattedCityName = cityName.charAt(0).toUpperCase() + cityName.slice(1).replace(/-/g, ' ');
  const formattedStateName = stateName.charAt(0).toUpperCase() + stateName.slice(1).replace(/-/g, ' ');

  const seoData = generateCompleteSEO({
    title: `Best Businesses in ${formattedCityName}, ${formattedStateName}`,
    description: `Find ${categories?.length || 0}+ business categories in ${formattedCityName}, ${formattedStateName}. Browse reviews, ratings and contact information for local businesses and services.`,
    keywords: generateLocationKeywords(formattedCityName, formattedStateName, null),
    url: `/in/${stateSlug}/${citySlug}`,
    type: 'website'
  });

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.meta.description}
        keywords={seoData.meta.keywords}
        canonical={seoData.link.canonical}
        robots={seoData.meta.robots}
        ogTitle={seoData.meta['og:title']}
        ogDescription={seoData.meta['og:description']}
        ogUrl={seoData.meta['og:url']}
        ogType={seoData.meta['og:type']}
        twitterTitle={seoData.meta['twitter:title']}
        twitterDescription={seoData.meta['twitter:description']}
        geoRegion={`IN-${stateSlug.toUpperCase()}`}
        geoPlacename={formattedCityName}
      />

      <CitySchema
        stateSlug={stateSlug}
        citySlug={citySlug}
        cityName={cityName}
        stateName={stateName}
        categories={categories}
      />
      <CategoryGrid
        categories={categories}
        cityName={cityName}
        stateSlug={stateSlug}
        citySlug={citySlug}
      />
    </>
  );
}


